import { useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface VariantMeta {
  id: string;
  name: string;
}

interface PrototypeSwitcherProps {
  variants: VariantMeta[];
  currentVariant: string;
  onSelectVariant: (id: string) => void;
}

export default function PrototypeSwitcher({
  variants,
  currentVariant,
  onSelectVariant,
}: PrototypeSwitcherProps) {
  const currentIndex = variants.findIndex(v => v.id === currentVariant);
  const activeVariant = variants[currentIndex] ?? variants[0];

  const goPrev = () => {
    const nextIdx = (currentIndex - 1 + variants.length) % variants.length;
    onSelectVariant(variants[nextIdx].id);
  };

  const goNext = () => {
    const nextIdx = (currentIndex + 1) % variants.length;
    onSelectVariant(variants[nextIdx].id);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, variants]);

  return (
    <aside aria-label="Prototype Variant Switcher" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
      <div className="bg-slate-900/95 text-white backdrop-blur-md px-4 py-2.5 rounded-full shadow-2xl border border-slate-700 flex items-center gap-3 select-none">
        <button
          onClick={goPrev}
          className="p-1.5 hover:bg-slate-800 rounded-full transition-colors active:scale-95 text-slate-300 hover:text-white"
          title="Previous variant (Left arrow)"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center min-w-[200px] text-center">
          <span className="text-xs uppercase font-bold tracking-wider text-pink-400">
            UI Prototype Switcher
          </span>
          <span className="text-sm font-semibold text-slate-100">
            Variant {activeVariant.id}: {activeVariant.name}
          </span>
        </div>

        <button
          onClick={goNext}
          className="p-1.5 hover:bg-slate-800 rounded-full transition-colors active:scale-95 text-slate-300 hover:text-white"
          title="Next variant (Right arrow)"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </aside>
  );
}
